// apps/web/src/features/clients/components/ClientContactsSection.tsx
//
// Contacts of one client, presented with the same weight as Legal entities:
// active contacts (primary first), then a labelled archive disclosure. Every
// mutate CTA disappears for a read-only actor or an archived parent, because
// the backend refuses those writes anyway (`CLIENT_ARCHIVED_READONLY`).
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientContact } from '@magimp/shared';
import { Button, SectionCard } from '../../../ui/index.js';
import { useTouchActionStyle } from '../useMobileFieldStyle.js';
import { ClientContactRow } from './ClientContactRow.js';
import { ContactFormModal } from './ContactFormModal.js';

export interface ClientContactsSectionProps {
  clientId: string;
  clientName: string;
  contacts: readonly ClientContact[];
  /** False for read-only actors and for an archived parent client. */
  canManage: boolean;
  /** True while the CLIENT's own lifecycle write is in flight (AC-10). */
  busy?: boolean;
}

const LIST_STYLE = {
  listStyle: 'none' as const,
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 'var(--s-2)',
};

export function ClientContactsSection({
  clientId,
  clientName,
  contacts,
  canManage,
  busy = false,
}: ClientContactsSectionProps) {
  const { t } = useTranslation(['clients', 'common']);
  const actionStyle = useTouchActionStyle();
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ClientContact | null>(null);
  const [showArchive, setShowArchive] = useState(false);

  const active = contacts.filter((c) => !c.archivedAt);
  const archived = contacts.filter((c) => !!c.archivedAt);
  // Primary first: it is the number an operator dials without reading further.
  const ordered = [...active].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));

  return (
    <SectionCard
      title={t('clients:sections.contacts')}
      count={active.length}
      action={
        canManage ? (
          <Button
            icon="plus"
            kind="secondary"
            size="sm"
            style={actionStyle}
            disabled={busy}
            onClick={() => setAddOpen(true)}
          >
            {t('clients:actions.addContact')}
          </Button>
        ) : undefined
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)', minWidth: 0 }}>
        {ordered.length === 0 ? (
          <p style={{ color: 'var(--fg-3)', margin: 0 }}>{t('clients:emptyContacts')}</p>
        ) : (
          <ul style={LIST_STYLE}>
            {ordered.map((c) => (
              <ClientContactRow
                key={c.id}
                clientId={clientId}
                contact={c}
                hasOtherActive={active.length > 1}
                canManage={canManage}
                busy={busy}
                onEdit={setEditTarget}
              />
            ))}
          </ul>
        )}

        {archived.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-2)' }}>
            <div>
              <Button
                kind="quiet"
                size="sm"
                style={actionStyle}
                aria-expanded={showArchive}
                onClick={() => setShowArchive((s) => !s)}
                data-testid="client-contacts-archive-toggle"
              >
                {showArchive
                  ? t('clients:actions.hideArchive')
                  : t('clients:actions.showArchive', { count: archived.length })}
              </Button>
            </div>
            {showArchive && (
              <ul style={LIST_STYLE}>
                {archived.map((c) => (
                  <ClientContactRow
                    key={c.id}
                    clientId={clientId}
                    contact={c}
                    hasOtherActive={active.length > 0}
                    canManage={canManage}
                    busy={busy}
                    onEdit={setEditTarget}
                  />
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {addOpen && (
        <ContactFormModal
          clientId={clientId}
          clientName={clientName}
          onClose={() => setAddOpen(false)}
        />
      )}
      {editTarget && (
        <ContactFormModal
          clientId={clientId}
          clientName={clientName}
          contact={editTarget}
          onClose={() => setEditTarget(null)}
        />
      )}
    </SectionCard>
  );
}
